// ========================================
// 📄 src/services/notificationService.js
// ========================================
import { errorHandler } from "../utils/errorHandler";

class NotificationService {
  constructor() {
    this.notifications = [];
    this.listeners = [];
  }

  // Ajouter un listener pour les notifications
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }

  // Notifier tous les listeners
  notifyListeners(notification) {
    this.listeners.forEach((listener) => {
      try {
        listener(notification);
      } catch (error) {
        console.error("Erreur dans le listener de notification:", error);
      }
    });
  }

  // Créer une notification
  createNotification(type, message, options = {}) {
    const notification = {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp: new Date(),
      duration: options.duration || 5000,
      persistent: options.persistent || false,
      actions: options.actions || [],
      ...options,
    };

    this.notifications.push(notification);
    this.notifyListeners(notification);

    // Auto-suppression si pas persistante
    if (!notification.persistent) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, notification.duration);
    }

    return notification.id;
  }

  // Méthodes de convenance
  success(message, options = {}) {
    return this.createNotification("success", message, {
      ...options,
      icon: "check-circle",
    });
  }

  error(message, options = {}) {
    return this.createNotification("error", message, {
      ...options,
      icon: "x-circle",
      persistent: true, // Les erreurs restent jusqu'à fermeture manuelle
    });
  }

  warning(message, options = {}) {
    return this.createNotification("warning", message, {
      ...options,
      icon: "alert-triangle",
    });
  }

  info(message, options = {}) {
    return this.createNotification("info", message, {
      ...options,
      icon: "info",
    });
  }

  // Supprimer une notification
  removeNotification(id) {
    this.notifications = this.notifications.filter((notif) => notif.id !== id);
    this.notifyListeners({ type: "remove", id });
  }

  // Supprimer toutes les notifications
  clearAll() {
    this.notifications = [];
    this.notifyListeners({ type: "clear" });
  }

  // Obtenir toutes les notifications
  getAll() {
    return [...this.notifications];
  }

  // Gestion des erreurs Firebase
  handleFirebaseError(error) {
    let message = "Une erreur s'est produite";

    switch (error.code) {
      case "auth/user-not-found":
        message = "Utilisateur non trouvé";
        break;
      case "auth/wrong-password":
        message = "Mot de passe incorrect";
        break;
      case "auth/email-already-in-use":
        message = "Cet email est déjà utilisé";
        break;
      case "auth/weak-password":
        message = "Le mot de passe doit contenir au moins 6 caractères";
        break;
      case "auth/invalid-email":
        message = "Adresse email invalide";
        break;
      case "auth/too-many-requests":
        message = "Trop de tentatives. Réessayez plus tard";
        break;
      case "permission-denied":
        message = "Permission refusée";
        break;
      case "unavailable":
        message = "Service temporairement indisponible";
        break;
      default:
        message = error.message || "Une erreur inattendue s'est produite";
    }

    this.error(message, {
      details:
        process.env.NODE_ENV === "development" ? error.message : undefined,
    });

    // Logger l'erreur pour le debugging
    errorHandler.logError(error, {
      context: "firebase",
      userAction: "unknown",
    });
  }

  // Gestion des erreurs réseau
  handleNetworkError(error) {
    this.error("Problème de connexion. Vérifiez votre connexion internet.", {
      persistent: true,
      actions: [
        {
          label: "Réessayer",
          action: () => window.location.reload(),
        },
      ],
    });
  }

  // Gestion des erreurs de validation
  handleValidationError(errors) {
    const errorMessages = Object.values(errors).join(", ");
    this.warning(`Erreurs de validation: ${errorMessages}`);
  }

  // Notifications de succès pour les actions courantes
  leadCreated(leadName) {
    this.success(`Prospect "${leadName}" créé avec succès`);
  }

  leadUpdated(leadName) {
    this.success(`Prospect "${leadName}" mis à jour`);
  }

  leadDeleted(leadName) {
    this.success(`Prospect "${leadName}" supprimé`);
  }

  interactionAdded() {
    this.success("Interaction ajoutée avec succès");
  }

  statusChanged(newStatus) {
    this.success(`Statut changé vers "${newStatus}"`);
  }

  dataExported(format) {
    this.success(`Données exportées en ${format.toUpperCase()}`);
  }

  // Notifications de chargement
  showLoading(message = "Chargement...") {
    return this.createNotification("loading", message, {
      persistent: true,
      icon: "loader",
    });
  }

  hideLoading(notificationId) {
    this.removeNotification(notificationId);
  }
}

// Instance singleton
export const notificationService = new NotificationService();

// Hook React pour utiliser le service
export const useNotificationService = () => {
  return {
    success: notificationService.success.bind(notificationService),
    error: notificationService.error.bind(notificationService),
    warning: notificationService.warning.bind(notificationService),
    info: notificationService.info.bind(notificationService),
    showLoading: notificationService.showLoading.bind(notificationService),
    hideLoading: notificationService.hideLoading.bind(notificationService),
    clearAll: notificationService.clearAll.bind(notificationService),
  };
};
